import React, { useState } from 'react';
import { Header } from '@/components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Download, Mail, Copy, Check, Sparkles, Heart,
  Type, Palette, Megaphone, Building2, Quote, Video,
} from 'lucide-react';
import { toast } from 'sonner';
import { Helmet } from '@dr.pogodin/react-helmet';
import SiteFooter from '@/components/SiteFooter';
import useCanonicalUrl from '@/hooks/useCanonicalUrl';

/**
 * Public press kit at /press. Built so journalists, podcasters,
 * partnership leads, and bloggers can self-serve everything they
 * normally email asking for: logos (light/dark/icon-only), brand
 * colors, founder bio, screenshots, one-liner blurbs.
 *
 * Entirely client-side — no backend route added. All downloads
 * generate locally via the same SVG → blob → download pattern
 * used by AdminSocialIconsTile.
 */

const PRESS_CONTACT_EMAIL = 'support@clearcommunicationapp.com';

const BRAND = {
  name: 'ClearCommunicationApp',
  tagline: 'Bridging neurodivergent communication.',
  tagline_es: 'Tendiendo puentes en la comunicación neurodivergente.',
  founded: '2025',
  founder: 'Katie · Founder & CEO (neurodivergent mom of 4 neurodivergent kids)',
  website: 'https://clearcommunicationapp.com',
  press_contact: PRESS_CONTACT_EMAIL,
};

const COLORS = [
  { name: 'Teal · Primary',  hex: '#00A896', usage: 'Buttons, primary CTAs, links' },
  { name: 'Aqua · Accent',   hex: '#00E5CC', usage: 'Highlights, badges, focus rings' },
  { name: 'Ink · Text',      hex: '#0F172A', usage: 'Body text on light surfaces' },
  { name: 'Cloud · Bg',      hex: '#F8FAFC', usage: 'Light backgrounds, cards' },
  { name: 'Coral · Warning', hex: '#FB7185', usage: 'Sparingly — alerts, errors' },
  { name: 'Lemon · Accent',  hex: '#FDE68A', usage: 'Friendly highlights, sticky notes' },
];

const BLURBS = [
  {
    label: 'One-liner',
    text: 'ClearCommunicationApp helps neurodivergent people translate everyday neurotypical communication — phrases, social cues, and tone — in plain language.',
  },
  {
    label: '30-word summary',
    text: 'ClearCommunicationApp is a phrase-library + AI thought-interpreter built by a neurodivergent mom of 4 neurodivergent kids. It decodes neurotypical communication for autistic, ADHD, and neurodivergent users with translations, audio, and gamified daily tools.',
  },
  {
    label: '60-word boilerplate',
    text: 'ClearCommunicationApp (clearcommunicationapp.com) is a neurodivergent-built communication-bridge app. Its free tier offers 3,400+ neurotypical-phrase translations with audio, AI-generated thought interpretation, a To-Do list, a Dino-Llama Pomodoro timer, and a community blog. VIP supporters get an ad-free, fully-translated (English + Spanish) experience plus AI history. The app was founded in 2025 by Katie, a neurodivergent mom of four neurodivergent children.',
  },
];

// Inline SVG logo definitions. The "icon" path is the same llama-dino
// pixel-art motif used across the app. Variants:
//  - full   :  Icon + wordmark
//  - icon   :  Standalone square icon (favicon-style)
//  - mono   :  Single-colour wordmark — for grayscale press use
const ICON_VIEWBOX = '0 0 64 64';
const ICON_PATH_PIXEL = (
  // Simple pixel-art llama silhouette built from rects — keeps SVG light
  <>
    <rect x="20" y="14" width="6" height="6" />
    <rect x="26" y="14" width="6" height="6" />
    <rect x="20" y="20" width="6" height="6" />
    <rect x="26" y="20" width="6" height="6" />
    <rect x="14" y="26" width="6" height="6" />
    <rect x="20" y="26" width="6" height="6" />
    <rect x="26" y="26" width="6" height="6" />
    <rect x="32" y="26" width="6" height="6" />
    <rect x="38" y="26" width="6" height="6" />
    <rect x="14" y="32" width="6" height="6" />
    <rect x="38" y="32" width="6" height="6" />
    <rect x="20" y="38" width="6" height="6" />
    <rect x="26" y="38" width="6" height="6" />
    <rect x="32" y="38" width="6" height="6" />
    <rect x="20" y="44" width="6" height="6" />
    <rect x="32" y="44" width="6" height="6" />
  </>
);

const LOGO_VARIANTS = [
  {
    id: 'full-color',
    name: 'Logo · Full color',
    desc: 'Detective Dino + teal wordmark · default placement',
    width: 1280,
    height: 320,
    // Full-color brand logo: the regular Detective Dino (sans-background
    // /dino-clean.png — magnifying glass + thought bubble) paired with
    // the brand-teal "ClearCommunicationApp" wordmark. Matches the
    // header treatment users see on the site itself.
    render: (ref) => (
      <svg ref={ref} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1280 320" width="1280" height="320">
        <rect width="1280" height="320" fill="transparent" />
        <image href="/dino-clean.png" x="20" y="20" width="280" height="280" preserveAspectRatio="xMidYMid meet" />
        <text x="330" y="170" fontFamily="system-ui,-apple-system,Segoe UI,Roboto,sans-serif" fontWeight="800" fontSize="84" fill="#00A896">ClearCommunicationApp</text>
        <text x="330" y="230" fontFamily="system-ui,-apple-system,Segoe UI,Roboto,sans-serif" fontWeight="500" fontSize="32" fill="#475569">Bridging neurodivergent communication</text>
      </svg>
    ),
  },
  {
    id: 'icon-only',
    name: 'Icon only',
    desc: 'Square avatar · 1024×1024 (Detective Dino circle PNG)',
    width: 1024,
    height: 1024,
    // Square / avatar variant. Uses the circle-cropped Dino bust which
    // is exactly what social profiles, App Store icons, and blog
    // bylines need.
    render: (ref) => (
      <svg ref={ref} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024" width="1024" height="1024">
        <image href="/dino-blog-circle.png" x="0" y="0" width="1024" height="1024" />
      </svg>
    ),
  },
  {
    id: 'dino-full-body',
    name: 'Dino · Full body',
    desc: 'Detective Dino sans-background · for hero/marketing art',
    width: 1024,
    height: 1024,
    // The clean Detective Dino PNG by itself — useful for marketing
    // hero shots where the wordmark sits elsewhere on the page.
    render: (ref) => (
      <svg ref={ref} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1024 1024" width="1024" height="1024">
        <rect width="1024" height="1024" fill="transparent" />
        <image href="/dino-clean.png" x="0" y="0" width="1024" height="1024" preserveAspectRatio="xMidYMid meet" />
      </svg>
    ),
  },
  {
    id: 'wordmark-teal',
    name: 'Wordmark · Teal',
    desc: 'Brand-color wordmark · for light backgrounds',
    width: 600,
    height: 80,
    render: (ref) => (
      <svg ref={ref} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 600 80" width="600" height="80">
        <rect width="600" height="80" fill="transparent" />
        <text x="0" y="56" fontFamily="system-ui,-apple-system,Segoe UI,Roboto,sans-serif" fontWeight="800" fontSize="48" fill="#00A896">ClearCommunicationApp</text>
      </svg>
    ),
  },
  {
    id: 'wordmark-mono-dark',
    name: 'Wordmark · Black',
    desc: 'For grayscale / light-bg press use',
    width: 600,
    height: 80,
    render: (ref) => (
      <svg ref={ref} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 600 80" width="600" height="80">
        <rect width="600" height="80" fill="transparent" />
        <text x="0" y="56" fontFamily="system-ui,-apple-system,Segoe UI,Roboto,sans-serif" fontWeight="800" fontSize="48" fill="#0F172A">ClearCommunicationApp</text>
      </svg>
    ),
  },
  {
    id: 'wordmark-mono-light',
    name: 'Wordmark · White',
    desc: 'For dark-bg press / merch use',
    width: 600,
    height: 80,
    background: '#0F172A',
    render: (ref) => (
      <svg ref={ref} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 600 80" width="600" height="80">
        <rect width="600" height="80" fill="#0F172A" />
        <text x="0" y="56" fontFamily="system-ui,-apple-system,Segoe UI,Roboto,sans-serif" fontWeight="800" fontSize="48" fill="#FFFFFF">ClearCommunicationApp</text>
      </svg>
    ),
  },
];

// ── Download helpers ─────────────────────────────────────────────────

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

/** Serialize an SVG DOM node to a string. Used to convert the
 * already-rendered <svg> in the page into a downloadable file —
 * avoids pulling react-dom/server into the client bundle. */
const serializeSvgElement = (el) => {
  if (!el) throw new Error('SVG node not found');
  return new XMLSerializer().serializeToString(el);
};

const svgStringToPngBlob = (svgString, width, height) =>
  new Promise((resolve, reject) => {
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => {
        URL.revokeObjectURL(url);
        blob ? resolve(blob) : reject(new Error('toBlob failed'));
      }, 'image/png');
    };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });

/**
 * Inline every `<image href="/some.png">` reference inside an SVG by
 * fetching the asset and replacing the href with a `data:image/...;base64`
 * URI. Why: when an SVG is serialised and loaded back into an <Image>
 * for canvas rasterisation, the canvas refuses to render external image
 * references (security model + the browser hasn't loaded them yet).
 * Inlining means the canvas sees a fully self-contained SVG and can
 * raster everything in one pass.
 *
 * Bug it fixes (May 2026): Press Kit `Dino · Full body` PNG download
 * produced a blank/broken PNG because the `<image href="/dino-clean.png">`
 * inside the SVG never rasterised on the canvas.
 */
const inlineSvgImages = async (svgEl) => {
  const clone = svgEl.cloneNode(true);
  const refs = clone.querySelectorAll('image');
  await Promise.all(
    Array.from(refs).map(async (node) => {
      const href = node.getAttribute('href') || node.getAttribute('xlink:href');
      if (!href || href.startsWith('data:')) return;
      try {
        const r = await fetch(href);
        if (!r.ok) throw new Error(`fetch ${href} → ${r.status}`);
        const blob = await r.blob();
        const dataUrl = await new Promise((res, rej) => {
          const fr = new FileReader();
          fr.onload = () => res(fr.result);
          fr.onerror = rej;
          fr.readAsDataURL(blob);
        });
        node.setAttribute('href', dataUrl);
        node.removeAttribute('xlink:href');
      } catch (e) {
        console.warn(`Could not inline ${href}:`, e);
      }
    }),
  );
  return clone;
};

// ── Page ────────────────────────────────────────────────────────────

const PressKitPage = () => {
  useCanonicalUrl();
  const [copiedKey, setCopiedKey] = useState(null);
  // Refs into the visible logo cards — each <svg> is keyed by variant.id
  // so the download handlers can find it and serialize via XMLSerializer.
  const svgRefs = React.useRef({});
  const setSvgRef = (id) => (el) => {
    if (el) svgRefs.current[id] = el;
  };

  const onCopy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      toast.success('Copied');
      setTimeout(() => setCopiedKey(null), 1500);
    } catch {
      toast.error('Copy failed — long-press the text to select manually');
    }
  };

  const onDownloadSvg = (variant) => {
    const el = svgRefs.current[variant.id];
    if (!el) return toast.error('Logo not ready yet — try again in a moment');
    const svgString = serializeSvgElement(el);
    downloadBlob(
      new Blob([svgString], { type: 'image/svg+xml' }),
      `cca-${variant.id}.svg`,
    );
  };

  const onDownloadPng = async (variant) => {
    const el = svgRefs.current[variant.id];
    if (!el) return toast.error('Logo not ready yet — try again in a moment');
    try {
      // Inline any <image href="…"> nodes first — required for the
      // Detective Dino variants whose PNG mascot would otherwise fail
      // to rasterise on the canvas.
      const inlined = await inlineSvgImages(el);
      const svgString = serializeSvgElement(inlined);
      const blob = await svgStringToPngBlob(svgString, variant.width, variant.height);
      downloadBlob(blob, `cca-${variant.id}-${variant.width}x${variant.height}.png`);
    } catch (e) {
      toast.error(`PNG export failed: ${e.message || e}`);
    }
  };

  const onDownloadAll = async () => {
    try {
      for (const v of LOGO_VARIANTS) {
        const el = svgRefs.current[v.id];
        if (!el) continue;
        const inlined = await inlineSvgImages(el);
        const svgString = serializeSvgElement(inlined);
        downloadBlob(new Blob([svgString], { type: 'image/svg+xml' }), `cca-${v.id}.svg`);
        await new Promise((r) => setTimeout(r, 100));
        const blob = await svgStringToPngBlob(svgString, v.width, v.height);
        downloadBlob(blob, `cca-${v.id}-${v.width}x${v.height}.png`);
        await new Promise((r) => setTimeout(r, 100));
      }
      toast.success(`Downloaded ${LOGO_VARIANTS.length} logo files (SVG + PNG)`);
    } catch (e) {
      toast.error(`Bulk download failed: ${e.message || e}`);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="press-kit-page">
      <Helmet>
        <title>Press Kit · ClearCommunicationApp</title>
        <meta
          name="description"
          content="Download ClearCommunicationApp logos, brand colors, founder bio, and press blurbs. Built for journalists, podcasters, and partnership leads."
        />
        <meta property="og:title" content="Press Kit · ClearCommunicationApp" />
        <meta property="og:description" content="Self-serve press kit: logos, colors, founder bio, blurbs." />
      </Helmet>
      <Header />

      <main className="mx-auto max-w-5xl px-4 py-8 sm:py-12 space-y-10" data-testid="press-kit-main">
        {/* Hero */}
        <section className="space-y-3" data-testid="press-kit-hero">
          <Badge variant="outline" className="text-[#00A896] border-[#00A896]/30">
            <Megaphone className="h-3 w-3 mr-1" /> Press Kit
          </Badge>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
            Everything you need to write about us
          </h1>
          <p className="text-base text-muted-foreground max-w-2xl">
            Logos, brand colors, founder bio, and ready-to-quote blurbs — all
            in one place. Skip the email, just download what you need.
          </p>
          <div className="flex flex-wrap gap-2 pt-2">
            <Button
              onClick={onDownloadAll}
              className="bg-[#00A896] hover:bg-[#008B7C] text-white"
              data-testid="download-everything-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              Download all logos (SVG + PNG)
            </Button>
            <Button
              variant="outline"
              asChild
              data-testid="press-contact-email-btn"
            >
              <a href={`mailto:${PRESS_CONTACT_EMAIL}?subject=Press inquiry — ClearCommunicationApp`}>
                <Mail className="h-4 w-4 mr-2" />
                Email press@…
              </a>
            </Button>
          </div>
        </section>

        <Separator />

        {/* Quick facts */}
        <section data-testid="press-kit-facts">
          <h2 className="text-xl font-bold mb-3 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-[#00A896]" /> Quick facts
          </h2>
          <Card>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 text-sm">
              <div><strong>Name:</strong> {BRAND.name}</div>
              <div><strong>Founded:</strong> {BRAND.founded}</div>
              <div><strong>Founder:</strong> {BRAND.founder}</div>
              <div><strong>Tagline:</strong> {BRAND.tagline}</div>
              <div className="sm:col-span-2">
                <strong>Website:</strong>{' '}
                <a href={BRAND.website} className="text-[#00A896] hover:underline" target="_blank" rel="noopener noreferrer">
                  {BRAND.website.replace('https://', '')}
                </a>
              </div>
              <div className="sm:col-span-2">
                <strong>Press contact:</strong>{' '}
                <a href={`mailto:${BRAND.press_contact}`} className="text-[#00A896] hover:underline">
                  {BRAND.press_contact}
                </a>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Logos */}
        <section data-testid="press-kit-logos">
          <h2 className="text-xl font-bold mb-3 flex items-center gap-2">
            <Type className="h-4 w-4 text-[#00A896]" /> Logos
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            Please don't recolor, distort, or place over busy backgrounds.
            Maintain at least 16 px clear space around the wordmark.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {LOGO_VARIANTS.map((v) => (
              <Card
                key={v.id}
                data-testid={`logo-variant-${v.id}`}
                style={v.background ? { background: v.background } : undefined}
              >
                <CardContent className="p-4 space-y-3">
                  <div
                    className="rounded border bg-white overflow-hidden flex items-center justify-center p-3 min-h-[100px]"
                    style={v.background ? { background: v.background, borderColor: '#1e293b' } : undefined}
                  >
                    <div className="max-w-full max-h-32 overflow-hidden flex items-center justify-center">
                      {v.render(setSvgRef(v.id))}
                    </div>
                  </div>
                  <div>
                    <div
                      className="text-sm font-semibold"
                      style={v.background ? { color: 'white' } : undefined}
                    >
                      {v.name}
                    </div>
                    <div
                      className="text-xs text-muted-foreground"
                      style={v.background ? { color: '#94a3b8' } : undefined}
                    >
                      {v.desc} · {v.width}×{v.height}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onDownloadSvg(v)}
                      data-testid={`logo-svg-${v.id}`}
                    >
                      <Download className="h-3.5 w-3.5 mr-1" /> SVG
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => onDownloadPng(v)}
                      data-testid={`logo-png-${v.id}`}
                    >
                      <Download className="h-3.5 w-3.5 mr-1" /> PNG
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Brand colors */}
        <section data-testid="press-kit-colors">
          <h2 className="text-xl font-bold mb-3 flex items-center gap-2">
            <Palette className="h-4 w-4 text-[#00A896]" /> Brand colors
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {COLORS.map((c) => (
              <Card key={c.hex} className="overflow-hidden" data-testid={`color-${c.hex.replace('#', '')}`}>
                <div
                  className="h-16 w-full cursor-pointer"
                  style={{ background: c.hex }}
                  onClick={() => onCopy(c.hex, c.hex)}
                  title={`Click to copy ${c.hex}`}
                />
                <CardContent className="p-2">
                  <div className="text-xs font-semibold">{c.name}</div>
                  <button
                    className="text-[11px] font-mono text-muted-foreground hover:text-foreground flex items-center gap-1"
                    onClick={() => onCopy(c.hex, c.hex)}
                    data-testid={`copy-hex-${c.hex.replace('#', '')}`}
                  >
                    {c.hex}
                    {copiedKey === c.hex
                      ? <Check className="h-3 w-3 text-green-600" />
                      : <Copy className="h-3 w-3" />}
                  </button>
                  <div className="text-[10px] text-muted-foreground mt-0.5">{c.usage}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Founder bio */}
        <section data-testid="press-kit-founder">
          <h2 className="text-xl font-bold mb-3 flex items-center gap-2">
            <Heart className="h-4 w-4 text-pink-500 fill-pink-500" /> Founder
          </h2>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Katie · Founder & CEO</CardTitle>
            </CardHeader>
            <CardContent className="text-sm space-y-2">
              <p>
                Katie is the founder of ClearCommunicationApp, a neurodivergent
                mom of four neurodivergent kids who built the app she wished
                had existed for her family. After years of feeling like she
                needed a "translator" for everyday neurotypical conversations,
                she taught herself to build, ship, and grow a global app from
                her kitchen table.
              </p>
              <p>
                She's available for podcast interviews, panel quotes, and
                guest essays on neurodivergent communication, women in tech,
                and parenting neurodivergent kids.
              </p>
              <div className="flex gap-2 pt-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onCopy(
                    'Katie · Founder & CEO of ClearCommunicationApp · neurodivergent mom of 4 neurodivergent kids',
                    'founder-line',
                  )}
                  data-testid="copy-founder-line"
                >
                  {copiedKey === 'founder-line'
                    ? <Check className="h-3.5 w-3.5 mr-1 text-green-600" />
                    : <Copy className="h-3.5 w-3.5 mr-1" />}
                  Copy one-liner
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Demo video — placeholder until a recorded clip is uploaded.
            Tracked in PRD as a press-kit completion task. The script
            and shot list are kept here so anyone (journalist, partner,
            internal team) can either record their own or follow the
            same flow we recommend.

            Why it matters: a 60-90 second screen-grab of the guided
            tour is the most concrete "what does this app actually do"
            asset journalists ask for. Embed once a clip is recorded. */}
        <section data-testid="press-kit-demo-video">
          <h2 className="text-xl font-bold mb-3 flex items-center gap-2">
            <Video className="h-4 w-4 text-[#00A896]" /> 60-second product demo
          </h2>
          <Card>
            <CardContent className="p-5 space-y-3">
              <p className="text-sm text-muted-foreground leading-relaxed">
                A short screen-grab of the in-app guided tour is the
                fastest way to show readers what ClearCommunicationApp
                does. Recording is coming soon — meanwhile, here&apos;s the
                shot list / script anyone covering us is welcome to use:
              </p>
              <ol className="text-sm space-y-1.5 list-decimal pl-5">
                <li><strong>0:00 – 0:08</strong> · Open the homepage at <code>clearcommunicationapp.com</code>. Show the @clearcommunicationapp linktree with the colorful pill links.</li>
                <li><strong>0:08 – 0:18</strong> · Tap &quot;Take a tour!&quot; → choose <em>English</em>. Walk through Logo → Install → Language → ☰ Menu.</li>
                <li><strong>0:18 – 0:30</strong> · Continue past Phrase Library → Thought Interpreter → Bottom Nav. Stop on the Library pill.</li>
                <li><strong>0:30 – 0:42</strong> · Library tour: 🇬🇧 English Dialects (15) + 🇪🇸 Spanish Dialects (13). Tap a category to show subcategories rolling out.</li>
                <li><strong>0:42 – 0:55</strong> · Open Thought Interpreter. Type: <em>&quot;I don&apos;t feel like going to the get together tonight like planned.&quot;</em> Pick tone <em>Polite</em>, dialect <em>North American English</em>. Hit Rephrase — show Detective Dino&apos;s rewrite.</li>
                <li><strong>0:55 – 1:00</strong> · End on the linktree with the &quot;Take a tour!&quot; button + Detective Dino help-bot floating in the corner.</li>
              </ol>
              <p className="text-xs text-muted-foreground italic">
                Record at 1280×720, 30fps, no system audio (silent demo
                is fine — captions are easier to translate). Email the
                clip to {PRESS_CONTACT_EMAIL} and we&apos;ll embed it here.
              </p>
            </CardContent>
          </Card>
        </section>

        {/* Press blurbs */}
        <section data-testid="press-kit-blurbs">
          <h2 className="text-xl font-bold mb-3 flex items-center gap-2">
            <Quote className="h-4 w-4 text-[#00A896]" /> Press blurbs
          </h2>
          <div className="space-y-3">
            {BLURBS.map((b, i) => (
              <Card key={b.label} data-testid={`blurb-${i}`}>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <Badge variant="secondary">{b.label}</Badge>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => onCopy(b.text, `blurb-${i}`)}
                      data-testid={`copy-blurb-${i}`}
                    >
                      {copiedKey === `blurb-${i}`
                        ? <Check className="h-3.5 w-3.5 mr-1 text-green-600" />
                        : <Copy className="h-3.5 w-3.5 mr-1" />}
                      Copy
                    </Button>
                  </div>
                  <p className="text-sm leading-relaxed">{b.text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </section>

        {/* Partnership CTA */}
        <section data-testid="press-kit-partnerships">
          <Card className="bg-[#00A896]/5 border-[#00A896]/30">
            <CardContent className="p-6 text-center space-y-3">
              <Building2 className="h-8 w-8 text-[#00A896] mx-auto" />
              <h3 className="text-lg font-bold">Looking to partner?</h3>
              <p className="text-sm text-muted-foreground max-w-xl mx-auto">
                We work with schools, therapists, neurodivergent advocacy
                groups, and accessibility-focused brands. Reach out and we'll
                set up a quick chat.
              </p>
              <Button
                asChild
                className="bg-[#00A896] hover:bg-[#008B7C] text-white"
                data-testid="press-kit-partner-btn"
              >
                <a href={`mailto:${PRESS_CONTACT_EMAIL}?subject=Partnership%20inquiry%20%E2%80%94%20ClearCommunicationApp`}>
                  <Mail className="h-4 w-4 mr-2" />
                  Start a conversation
                </a>
              </Button>
            </CardContent>
          </Card>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
};

export default PressKitPage;
